<script setup lang="ts">
/**
 * LegalModal — 회원가입 약관·개인정보 동의의 인라인 표시(모달/바텀시트).
 *
 * 새 탭(target=_blank) 대신 폼 컨텍스트를 유지한 채 약관을 확인하게 한다(UX):
 *  - 데스크톱: 중앙 모달  /  모바일(md 이하): 하단 바텀시트
 *  - 본문은 LegalContent(SSoT) 재사용 — legal/* 페이지와 동일 카피, 복제 없음
 *  - 접근성: reka-ui Dialog 가 focus trap·ESC 닫기·배경 스크롤 잠금·aria-modal 자동 처리
 *
 * 약관 페이지(/legal/*)는 SEO·직접링크용으로 그대로 존치 — 본 모달은 가입 흐름 전용 표시 채널.
 */
import { X } from 'lucide-vue-next';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';

import LegalContent from '#/components/fo/LegalContent.vue';
import type { LegalDoc } from '#/data/legal';

interface Props {
  /** 모달 열림 상태(부모 v-model). */
  open: boolean;
  /** 표시할 법률 문서(legal.ts SSoT). */
  doc: LegalDoc;
  /** 본문 아이콘 종류. */
  icon?: 'terms' | 'privacy';
}

withDefaults(defineProps<Props>(), { icon: 'terms' });

defineEmits<{
  /** 열림 상태 변경(v-model:open). */
  'update:open': [value: boolean];
}>();
</script>

<template>
  <DialogRoot :open="open" @update:open="$emit('update:open', $event)">
    <DialogPortal>
      <!-- 배경 오버레이(클릭·ESC 로 닫힘은 reka-ui 가 처리) -->
      <DialogOverlay
        class="fixed inset-0 z-modal bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fo-fade"
      />

      <!-- 데스크톱: 중앙 모달 / 모바일(md 이하): 하단 바텀시트 -->
      <DialogContent
        class="fixed bottom-0 left-1/2 z-modal flex max-h-[85vh] w-full -translate-x-1/2 flex-col overflow-hidden rounded-t-[20px] border border-line bg-white shadow-lg focus:outline-none md:bottom-auto md:top-1/2 md:max-h-[80vh] md:max-w-[640px] md:-translate-y-1/2 md:rounded-modal"
      >
        <!-- 헤더(제목 + 닫기) -->
        <div class="flex items-center justify-between border-b border-line px-6 py-5 md:px-8">
          <DialogTitle
            class="m-0 font-serif text-[18px] font-extrabold tracking-[-0.02em] text-brand md:text-[20px]"
          >
            {{ doc.title }}
          </DialogTitle>
          <DialogClose
            class="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white text-brand transition-colors hover:border-accent hover:text-accent-deep"
            aria-label="닫기"
          >
            <X class="h-[18px] w-[18px]" :stroke-width="2" />
          </DialogClose>
        </div>

        <!-- 본문(스크롤) — LegalContent SSoT 재사용, 모달은 자체 헤더가 있어 showHeader=false -->
        <div class="overflow-y-auto px-6 py-6 md:px-8">
          <DialogDescription class="sr-only">
            {{ doc.title }} 전문을 확인할 수 있는 대화 상자입니다.
          </DialogDescription>
          <LegalContent :doc="doc" :icon="icon" :show-header="false" />
        </div>

        <!-- 푸터(확인 닫기) -->
        <div class="border-t border-line px-6 py-4 md:px-8">
          <DialogClose
            class="flex h-11 w-full items-center justify-center rounded-[10px] bg-brand text-[14px] font-bold text-white transition-colors hover:bg-brand-2"
          >
            확인했어요
          </DialogClose>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
